import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter, Link } from 'react-router-dom'
import omit from 'lodash/omit'
import forOwn from 'lodash/forOwn'
import { History } from 'history'
import moment from 'moment'

import { fetchUsers } from './userActions'
import {
  previousPage,
  goToPage,
  nextPage,
} from '../../helpers/paginationHelper'
import { selectUserIndex, selectUserMeta } from '../../reducers/users'
import {
  parseQuery,
  stringifyQuery,
  toggleSort,
} from '../../helpers/queryHelper'
import { redirectToSingleResult } from '../../helpers/indexPageHelper'
import { displayNotification } from '../../actions/notificationsActions'
import { User } from '../../core/types'
import IndexHeaderRow from '../common/indexes/indexHeaderRow'

import Breadcrumb from '../../components/Breadcrumb'
import TablePagination from '../../components/tablePagination'
import FilterBadge from '../../components/filterBadge'
import LoadingSection from '../../components/loadingSection'

export type Callback<T = void> = (...args: any) => T

export interface Props {
  currentUsers: User[]
  location: { key: any } & Location
  history: History
  displayNotification: Callback
  /** Dispatches the fetchUsers thunk and returns its Promise */
  fetchUsers: Callback<Promise<any>>
  meta: { total: number }
}

interface UserIndexState {
  loading: boolean
}

export class UserIndex extends Component<Props, UserIndexState> {
  static displayName = 'UserIndex'

  state: UserIndexState = {
    loading: true,
  }

  componentWillMount() {
    this.loadUsers()
  }

  componentDidUpdate(prevProps) {
    const {
      currentUsers,
      history,
      displayNotification,
      location: { search },
    } = this.props
    if (search !== prevProps.location.search) {
      this.loadUsers()
    }
    if (process.env.NODE_ENV !== 'test') {
      redirectToSingleResult(
        currentUsers,
        '/users',
        history,
        displayNotification
      )
    }
  }

  loadUsers = async () => {
    const { location, history } = this.props
    if (!location.search) {
      history.push({
        pathname: '/users',
        search: stringifyQuery({ offset: 0, limit: 25 }),
      })
      return
    }
    await this.fetchUsersWithFilters(location.search)
  }

  fetchUsersWithFilters = async (query) => {
    const { fetchUsers } = this.props
    const parsedQuery = parseQuery(query)
    this.setState({ loading: true })
    try {
      if (parsedQuery.phone || parsedQuery.email) {
        const formattedNumber = parsedQuery.phone
          ? JSON.stringify(parsedQuery.phone)
          : null
        const formattedEmail =
          parsedQuery.email && typeof parsedQuery.email !== 'string'
            ? JSON.stringify(parsedQuery.email)
            : parsedQuery.email
        const formattedQuery = {
          ...parsedQuery,
          phone: formattedNumber,
          email: formattedEmail,
        }
        await fetchUsers(formattedQuery)
      } else {
        await fetchUsers(parsedQuery)
      }
    } finally {
      this.setState({ loading: false })
    }
  }

  headerClick = (sortEnum) => {
    const {
      location: { search },
    } = this.props
    this.props.history.push({
      pathname: '/users',
      search: toggleSort(search, sortEnum, 'DESC'),
    })
  }

  prevPageClick = () => {
    const {
      location: { search },
    } = this.props
    previousPage(search, this.props.history, '/users')
  }

  onPageClick = (pageNum) => {
    const {
      location: { search },
    } = this.props
    goToPage(search, this.props.history, pageNum, '/users')
  }

  nextPageClick = () => {
    const {
      currentUsers,
      location: { search },
    } = this.props
    nextPage(search, this.props.history, currentUsers.length, '/users')
  }

  filtersClick = () => {
    const {
      location: { search },
    } = this.props
    this.props.history.push({
      pathname: '/users/filters',
      search,
    })
  }

  countFilters = (query, defaultFilters) => {
    let count = 0

    forOwn(omit(query, defaultFilters), (obj) => {
      if (Array.isArray(obj)) {
        count += (obj as any[]).length
      } else {
        count += 1
      }
    })

    return count
  }

  filterBadge = () => {
    const {
      location: { search },
    } = this.props
    const parsedQuery = parseQuery(search)
    const defaultFilters = ['offset', 'limit']
    const count = this.countFilters(parsedQuery, defaultFilters)

    if (count > 0 && count < 10) {
      return <span className="index-table__filter-badge">{count}</span>
    } else if (count >= 10) {
      return (
        <span className="index-table__filter-badge index-table__filter-badge--multi">
          {count}
        </span>
      )
    } else {
      return <span className="index-table__filter-icon icon-filters" />
    }
  }

  openUser = (id: string) => {
    this.props.history.push(`/users/${id}`)
  }

  render() {
    const { currentUsers, meta, location } = this.props
    const { total } = meta
    const { search } = location
    const parsedSearch = parseQuery(search)
    const totalPages = Math.ceil(total / parsedSearch.limit)

    const levels = [
      { label: 'Index ', path: '/' },
      {
        label: 'People',
        path: '/users',
      },
    ]

    const headerList = [
      { displayHeader: 'NAME', sortable: true, sortEnum: 'LAST_FIRST' },
      { displayHeader: 'ADDRESS', sortable: true, sortEnum: 'ADDRESS' },
      { displayHeader: 'CITY', sortable: true, sortEnum: 'CITY' },
      { displayHeader: 'STATE', sortable: true, sortEnum: 'STATE' },
      { displayHeader: 'ZIP', sortable: true, sortEnum: 'ZIP' },
      { displayHeader: 'QUOTE DATE', sortable: true, sortEnum: 'QUOTED_AT' },
      { displayHeader: 'STATUS', sortable: true, sortEnum: 'STATUS' },
      { displayHeader: 'AGENT', sortable: true, sortEnum: 'AGENT_LAST_FIRST' },
    ]

    const rows = currentUsers.map((user, index) => {
      const {
        id,
        firstName,
        lastName,
        addresses,
        currentAdmin,
        productPackage,
      } = user
      const address = (addresses || [])[0]
      const { line1, line2, city, state, zip } = address || {}

      return (
        <tr
          className="index-table__row"
          data-testid="userIndexRow"
          key={index}
          onClick={() => this.openUser(id)}
        >
          <td className="index-table__cell">{`${lastName}, ${firstName}`}</td>
          {address ? (
            <td className="index-table__cell">
              {line1} {line2}
            </td>
          ) : (
            <td />
          )}
          {address ? <td className="index-table__cell">{city}</td> : <td />}
          {address ? <td className="index-table__cell">{state}</td> : <td />}
          {address ? <td className="index-table__cell">{zip}</td> : <td />}
          {productPackage?.quotedAt ? (
            <td className="index-table__cell">
              {moment(productPackage.quotedAt).format('MM/DD/YYYY')}
            </td>
          ) : (
            <td />
          )}
          {productPackage ? (
            <td className="index-table__cell">
              {productPackage.status.toUpperCase()}
            </td>
          ) : (
            <td />
          )}
          {currentAdmin ? (
            <td className="index-table__cell">
              {`${currentAdmin.lastName}, ${currentAdmin.firstName}`}
            </td>
          ) : (
            <td />
          )}
        </tr>
      )
    })

    return (
      <>
        <Breadcrumb levels={levels} />
        <LoadingSection
          isLoading={
            process.env.NODE_ENV === 'test' ? false : this.state.loading
          }
        >
          <div className="index index__drive-america" key={location.key}>
            <h1 className="index-table__header">
              People
              <span className="index-table__header--total">{total}</span>
            </h1>
            <div className="index-table__drive-america">
              <a
                data-testid="FiltersLink"
                onClick={this.filtersClick}
                className="index-table__filter-button"
              >
                <FilterBadge renderProps={this.props} />
                filters
              </a>
              <Link
                to="/refiapp"
                className="button button-drive-america link-drive-america link-drive-america__new-applicant"
              >
                New Applicant
              </Link>
            </div>

            <table className="index-table">
              <IndexHeaderRow
                headerList={headerList}
                parsedSearch={parsedSearch}
                clickCallback={this.headerClick}
              />
              <tbody className="index-table__body">{rows}</tbody>
            </table>
            <TablePagination
              search={location.search}
              queryLength={currentUsers.length}
              totalPages={totalPages}
              prevPageClick={this.prevPageClick}
              onPageClick={this.onPageClick}
              nextPageClick={this.nextPageClick}
            />
          </div>
        </LoadingSection>
      </>
    )
  }
}

export const mapStateToProps = (state) => {
  return {
    currentUsers: selectUserIndex(state.users),
    meta: selectUserMeta(state.users),
  }
}

export default withRouter(
  connect(mapStateToProps, {
    fetchUsers,
    displayNotification,
  })(UserIndex)
)
